import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import type { CurrencyCode } from '@/lib/formatPrice';

interface CurrencyContextValue {
  displayCurrency: CurrencyCode;
  setDisplayCurrency: (c: CurrencyCode) => void;
  /** ETB → USD rate (e.g. 0.0078 means 1 ETB ≈ 0.0078 USD) */
  rate: number;
  /** Inverse helper: how many ETB equals 1 USD */
  usdToEtb: number;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

const STORAGE_KEY = 'eer.displayCurrency';

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { settings } = useSystemSettings();
  const rate = Number(settings?.etb_to_usd_rate?.value ?? 0.0078) || 0.0078;

  const [displayCurrency, setDisplayCurrencyState] = useState<CurrencyCode>(() => {
    if (typeof window === 'undefined') return 'ETB';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'USD' ? 'USD' : 'ETB';
  });

  const setDisplayCurrency = (c: CurrencyCode) => {
    setDisplayCurrencyState(c);
    try {
      window.localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* ignore */
    }
  };

  // Keep storage in sync if changed in another tab
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        setDisplayCurrencyState(e.newValue === 'USD' ? 'USD' : 'ETB');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <CurrencyContext.Provider
      value={{ displayCurrency, setDisplayCurrency, rate, usdToEtb: rate ? 1 / rate : 128 }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Fallback for components rendered outside provider (tests, etc.)
    return { displayCurrency: 'ETB', setDisplayCurrency: () => {}, rate: 0.0078, usdToEtb: 128 };
  }
  return ctx;
}
